import React, {useContext, useEffect, useState} from 'react';
import * as cognito from '../libs/cognito';
import {
    OperationalMetricType,
    RevenueType,
    useMyUserLazyQuery,
    useOperationalMetricTypeLazyQuery,
    useRevenueTypesLazyQuery
} from "../__generated__/generated_types";
import {AuthUser} from "./auth_types";
import {removeWorkspaceClientIdFromStore, saveWorkspaceClientIdToStore} from "../libs/workspace";
import Loading from "../components/loading/Loadiing";
import {redirectBrowser} from "../global/browser_functions";
import {useBudgetYearState} from "./budgetYearState";

export enum AuthStatus {
    Loading,
    SignedIn,
    SignedOut,
    SigningOut
}

export interface IAuth {
    user: AuthUser | null,
    budgetYear: number,
    budgetYearsList: number[];
    setBudgetYear: (year: number) => void;
    authStatus?: AuthStatus
    signInWithEmail: (email: string, password: string) => Promise<void>;
    signInWithPasswordReset?: (user: string, oldPassword: string, newPassword: string) => Promise<void>;
    signInWithCallback: (urlCode: string) => Promise<void>;
    signOut?: any
    verifyCode?: any
    getSession?: any
    sendCodeForgotPassword?: any
    changePassword?: any
    getAttributes?: any
    setAttribute?: any;

    setPasswordAndLogin: (username: string, code: string, password: string) => Promise<void>
    username?: string;
    setUsername: (username: string) => void;
    resendCode: () => void;
    reloadUserWithProperties: () => void;
    switchUserClientId: (clientId: string) => void;

    operationalMetricTypes: Pick<OperationalMetricType, 'id' | 'name'>[];
    revenueTypes: RevenueType[];
}

export type {AuthUser} from "./auth_types";

const defaultState: IAuth = {
    budgetYear: 0,
    budgetYearsList: [],
    setBudgetYear: () => {/* */},
    authStatus: AuthStatus.Loading,
    operationalMetricTypes: [],
    revenueTypes: [],
    user: null,
    signInWithEmail: () => Promise.resolve(),
    signInWithCallback: () => Promise.resolve(),
    setPasswordAndLogin: async () => {/* Do nothing */},
    setUsername: () => {/* */},
    resendCode: () => {/* */},
    reloadUserWithProperties: () => {/* */},
    switchUserClientId: () => {/* */},
};

export const AuthContext = React.createContext(defaultState);

export const AuthIsSignedIn: React.FunctionComponent = ({ children }) => {
    const { authStatus }: IAuth = useContext(AuthContext);

    return <>{authStatus === AuthStatus.SignedIn ? children : null}</>;
};

export const AuthIsNotSignedIn: React.FunctionComponent = ({ children }) => {
    const { authStatus }: IAuth = useContext(AuthContext);

    return <>{authStatus === AuthStatus.SignedOut ? children : null}</>;
};

export function AuthIsSigningOut({children}: {children: React.ReactNode}): React.ReactElement {
    const { authStatus }: IAuth = useContext(AuthContext);

    if(authStatus === AuthStatus.SigningOut) {
        return <>{children}</>;
    }
    return <></>;
}

const AuthProvider: React.FunctionComponent = ({ children }) => {
    const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
    const [username, setUsername] = useState<string>();
    const [user, setUser] = useState<AuthUser | null>(null);
    const [operationalMetricTypes, setOperationalMetricTypes] = useState<Pick<OperationalMetricType, 'id' | 'name'>[]>([]);
    const [revenueTypes, setRevenueTypes] = useState<RevenueType[]>([]);

    const [getMyUser, {data: myUserResult, loading: myUserLoading, error: myUserError}] = useMyUserLazyQuery({
        fetchPolicy: "no-cache"
    });
    const budgetYearState = useBudgetYearState({
        yearsListData: myUserResult,
        yearsListLoading: myUserLoading
    });

    const [getOperationalMetricType, {data: operationalMetricTypeResult, loading: operationalMetricTypeLoading, error: operationalMetricTypeError}] = useOperationalMetricTypeLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [getRevenueTypes, {data: revenueTypesResult, loading: revenueTypesLoading, error: revenueTypesError}] = useRevenueTypesLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        if(!myUserLoading && myUserResult) {
            if(myUserResult.myUser) {
                const user = myUserResult.myUser;
                const userWithFeatures: AuthUser = {
                    ...user,
                    features: [],
                    clientFeatures: myUserResult.getClientFeatures
                };
                setUser(userWithFeatures);
                setAuthStatus(AuthStatus.SignedIn);
            }
            else {
                removeWorkspaceClientIdFromStore();
                setAuthStatus(AuthStatus.SignedOut);
            }
        }
    }, [myUserResult, myUserLoading]);

    useEffect(() => {
        if(!myUserLoading && myUserError) {
            signOut();
        }
    }, [myUserError]);

    useEffect(() => {
        setOperationalMetricTypes(operationalMetricTypeResult?.operationalMetricTypes as (Pick<OperationalMetricType, 'id' | 'name'>[]));
    }, [operationalMetricTypeResult?.operationalMetricTypes]);

    useEffect(() => {
        if (revenueTypesResult?.revenueTypes === undefined) {
            return;
        }

        /**
         * Note: Type-casting because the backend simply packs the RevenueType enum keys
         * into a list of strings and sends that to the frontend, so string[] can be cast to
         * RevenueType[]
        */
        setRevenueTypes(revenueTypesResult?.revenueTypes as RevenueType[]);
    }, [revenueTypesResult?.revenueTypes]);

    useEffect(() => {
        async function getSessionInfo() {
            const idToken: string | undefined = await cognito.refreshIdToken();

            if (idToken) {
                getMyUser({variables: {budgetYear: budgetYearState.budgetYear}});
                getOperationalMetricType();
                getRevenueTypes();
            } else {
                setAuthStatus(AuthStatus.SignedOut);
            }
        }
        if(authStatus === AuthStatus.Loading) {
            getSessionInfo().catch((err) => {
                console.log("[AuthContext] failed to obtain idToken, ", err);
                setAuthStatus(AuthStatus.SignedOut);
            });
        }
    }, [authStatus]);

    const isBudgetYearSwitching = !user && authStatus === AuthStatus.SignedIn;
    if (authStatus === AuthStatus.Loading || isBudgetYearSwitching) {
        return <Loading/>;
    }

    function reloadUserWithProperties(budgetYearArg?: number | undefined) {
        const budgetYear = budgetYearArg ?? budgetYearState.budgetYear;
        if (budgetYear) {
            getMyUser({
                variables: {
                    budgetYear: budgetYear
                }
            });
        }
    }

    function switchUserClientId(clientId: string) {
        if (user && user.clientId !== clientId) {
            saveWorkspaceClientIdToStore(clientId);
            if (budgetYearState.budgetYear) {
                budgetYearState.ensureBudgetYearIsValid(budgetYearState.budgetYear, clientId);
            }
            setAuthStatus(AuthStatus.Loading);
        }
    }

    function switchBudgetYear(budgetYear: number) {
        if (budgetYearState.budgetYear != budgetYear) {
            setUser(null);
            budgetYearState.setBudgetYear(budgetYear);
            reloadUserWithProperties(budgetYear);
        }
    }

    async function signInWithEmail(username: string, password: string) {
        try {
            removeWorkspaceClientIdFromStore();
            await cognito.signInWithEmail(username, password);
            setAuthStatus(AuthStatus.Loading);
        } catch (err) {
            setAuthStatus(AuthStatus.SignedOut);
            throw err;
        }
    }

    async function signInWithPasswordReset(username: string, oldPassword: string, newPassword: string) {
        try {
            removeWorkspaceClientIdFromStore();
            await cognito.signInWithPasswordReset(username, oldPassword, newPassword);
            setAuthStatus(AuthStatus.Loading);
        } catch (err) {
            setAuthStatus(AuthStatus.SignedOut);
            throw err;
        }
    }

    async function signInWithCallback(urlCode: string): Promise<void> {
        try {
            removeWorkspaceClientIdFromStore();
            await cognito.signInWithCode(urlCode);
            setAuthStatus(AuthStatus.Loading);
        } catch(err) {
            setAuthStatus(AuthStatus.SignedOut);
            throw err;
        }
    }

    function signOut() {
        budgetYearState.clearStoredBudgetYear();
        cognito.signOut();
        setAuthStatus(AuthStatus.SigningOut);
        redirectBrowser(cognito.getLogoutUrl());
    }

    async function verifyCode(username: string, code: string) {
        cognito.verifyCode( username, code );
    }

    async function sendCodeForgotPassword(username: string) {
        await cognito.sendCodeForgotPassword(username);
    }

    async function setPasswordAndLogin(username: string, code: string, password: string): Promise<void> {
        await cognito.confirmPassword(username, code, password);
        await signInWithEmail(username, password);
    }

    async function changePassword(oldPassword: string, newPassword: string) {
        await cognito.changePassword(oldPassword, newPassword);
    }

    async function resendCode() {
        if (!username) {
            throw Error("username undefined");
        }
        await cognito.resendCode(username);
    }

    // make sure the budget year is set before rendering the app for signed in users
    if (!budgetYearState.budgetYear && authStatus === AuthStatus.SignedIn) {
        // If the budget year is not set, we need to wait for the budget year
        // to be set as alot of the app depends on the budget year being set.
        return <Loading/>;
    }

    const state: IAuth = {
        budgetYear: budgetYearState.budgetYear ?? 0, // 0 is a placeholder for the budget year when not signed in as we don't care about the budget year when not signed in
        budgetYearsList: budgetYearState.yearsList,
        setBudgetYear: switchBudgetYear,
        authStatus,
        user,
        signInWithEmail,
        signInWithCallback,
        signInWithPasswordReset,
        signOut,
        verifyCode,
        sendCodeForgotPassword,
        setPasswordAndLogin,
        changePassword,
        username,
        setUsername,
        resendCode,
        operationalMetricTypes,
        revenueTypes,
        reloadUserWithProperties,
        switchUserClientId,
    };

    return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
